import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';

const GOOGLE_OAUTH_CLIENT_ID = process.env.REACT_APP_OAUTH_CLIENT_ID || process.env.OAUTH_CLIENT_ID;

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: {
            client_id: string;
            callback: (response: { credential?: string }) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme: string;
              size: string;
              width?: number;
              text?: string;
              type?: string;
            }
          ) => void;
        };
      };
    };
  }
}

type GoogleAuthButtonProps = {
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  onSuccess: (result: {
    created?: boolean;
    user?: { id?: string | number };
  }) => void;
  onError: (message: string) => void;
};

const GOOGLE_SCRIPT_ID = 'google-identity-services';
const DEFAULT_GOOGLE_BUTTON_WIDTH = 360;
const USE_NATIVE_GOOGLE_AUTH = Capacitor.getPlatform() === 'android';
let nativeGoogleInitializePromise: Promise<void> | null = null;

const loadGoogleIdentityScript = () =>
  new Promise<void>((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }

    const existingScript = document.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google sign-in')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google sign-in'));
    document.body.appendChild(script);
  });

const initializeNativeGoogleAuth = () => {
  if (!GOOGLE_OAUTH_CLIENT_ID) {
    throw new Error('Google sign-in is not configured');
  }

  if (!nativeGoogleInitializePromise) {
    nativeGoogleInitializePromise = SocialLogin.initialize({
      google: {
        webClientId: GOOGLE_OAUTH_CLIENT_ID,
        mode: 'online',
      },
    });
  }

  return nativeGoogleInitializePromise;
};

const getGoogleButtonText = (text: GoogleAuthButtonProps['text']) => {
  switch (text) {
    case 'signup_with':
      return 'Sign up with Google';
    case 'continue_with':
      return 'Continue with Google';
    case 'signin_with':
    default:
      return 'Sign in with Google';
  }
};

const isNativeGoogleCancellation = (e: any) =>
  e?.code === 'USER_CANCELLED' || /cancelled by user/i.test(e?.message || '');

const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({ text = 'signin_with', onSuccess, onError }) => {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(!USE_NATIVE_GOOGLE_AUTH);

  const handleNativeGoogleSignIn = useCallback(async () => {
    try {
      setLoading(true);
      await initializeNativeGoogleAuth();

      const response = await SocialLogin.login({
        provider: 'google',
        options: {},
      });

      const credential = 'idToken' in response.result ? response.result.idToken : null;
      if (!credential) {
        throw new Error('Google did not return a sign-in token');
      }

      const result = await api.loginWithGoogle(credential);
      onSuccess(result);
    } catch (e: any) {
      if (!isNativeGoogleCancellation(e)) {
        onError(e?.message || 'Google sign-in failed');
      }
    } finally {
      setLoading(false);
    }
  }, [onError, onSuccess]);

  useEffect(() => {
    if (USE_NATIVE_GOOGLE_AUTH) return;

    let cancelled = false;

    const initializeGoogleButton = async () => {
      try {
        setLoading(true);
        if (!GOOGLE_OAUTH_CLIENT_ID) {
          throw new Error('Google sign-in is not configured');
        }

        await loadGoogleIdentityScript();


        const buttonElement = buttonRef.current;
        if (cancelled || !buttonElement || !window.google?.accounts?.id) return;

        window.google.accounts.id.initialize({
          client_id: GOOGLE_OAUTH_CLIENT_ID,
          callback: async ({ credential }) => {
            if (!credential) {
              onError('Google did not return a sign-in token');
              return;
            }

            try {
              const result = await api.loginWithGoogle(credential);
              onSuccess(result);
            } catch (e: any) {
              onError(e?.message || 'Google sign-in failed');
            }
          },
        });

        buttonElement.innerHTML = '';
        window.google.accounts.id.renderButton(buttonElement, {
          theme: 'outline',
          size: 'large',
          width: Math.round(buttonElement.getBoundingClientRect().width) || DEFAULT_GOOGLE_BUTTON_WIDTH,
          text,
        });
      } catch (e: any) {
        if (!cancelled) {
          onError(e?.message || 'Google sign-in failed to initialize');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initializeGoogleButton();

    return () => {
      cancelled = true;
    };
  }, [onError, onSuccess, text]);

  if (USE_NATIVE_GOOGLE_AUTH) {
    return (
      <div className="google-auth-button-wrap">
        <button
          type="button"
          className="google-auth-native-button"
          disabled={loading}
          onClick={handleNativeGoogleSignIn}
        >
          {loading ? 'Signing in...' : getGoogleButtonText(text)}
        </button>
      </div>
    );
  }

  return (
    <div className="google-auth-button-wrap">
      {loading && <button type="button" className="google-auth-placeholder" disabled>Loading Google...</button>}
      <div ref={buttonRef} className={loading ? 'google-auth-button is-loading' : 'google-auth-button'} />
    </div>
  );
};

export default GoogleAuthButton;
